import React, { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, BarChart3, CalendarCheck2 } from "lucide-react";
import "./ExportingData.css";

import { getAllApplications } from "../services/applicationService";

const statuses = [
  "All",
  "APPLIED",
  "SHORTLISTED",
  "INTERVIEW_SCHEDULED",
  "INTERVIEW_COMPLETED",
  "REJECTED",
  "SELECTED",
];

const ExportingData = () => {

  const [applications, setApplications] = useState([]);
  const [jobFilter, setJobFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ================= FETCH REAL DATA =================
  const fetchApplications = async () => {

    try {

      const res = await getAllApplications();

      const normalized = (Array.isArray(res) ? res : []).map((item) => ({
          id: item.id || item.applicationId,
          candidate: item.fullName || item.candidateName || "-",
          email: item.email || "-",
          job: item.jobTitle || item.job?.title || "-",
          status: item.status || "APPLIED",
          date: item.createdAt
            ? new Date(item.createdAt).toISOString().slice(0, 10)
            : "",
        }));

      setApplications(normalized);

    } catch (error) {

      console.error("Failed to load applications", error);
      setApplications([]);

    }

  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // ================= JOB OPTIONS =================
  const jobOptions = useMemo(
    () => ["All", ...new Set(applications.map((item) => item.job))],
    [applications]
  );

  // ================= FILTERED DATA =================
  const filteredApplications = useMemo(() => {

    return applications.filter((item) => {

      const matchJob = jobFilter === "All" || item.job === jobFilter;

      const matchStatus =
        statusFilter === "All" || item.status === statusFilter;

      const matchFrom = !fromDate || item.date >= fromDate;

      const matchTo = !toDate || item.date <= toDate;

      return matchJob && matchStatus && matchFrom && matchTo;

    });

  }, [applications, jobFilter, statusFilter, fromDate, toDate]);

  // ================= REPORT STATS =================
  const reportStats = useMemo(() => {

    const total = filteredApplications.length;

    const hired = filteredApplications.filter(
      (item) => item.status === "SELECTED"
    ).length;

    const rejected = filteredApplications.filter(
      (item) => item.status === "REJECTED"
    ).length;

    const scheduled = filteredApplications.filter(
      (item) => item.status === "INTERVIEW_SCHEDULED"
    ).length;

    const completed = filteredApplications.filter(
      (item) => item.status === "INTERVIEW_COMPLETED"
    ).length;

    return { total, hired, rejected, scheduled, completed };

  }, [filteredApplications]);

  const handleReset = () => {
    setJobFilter("All");
    setStatusFilter("All");
    setFromDate("");
    setToDate("");
  };

  // ================= EXPORT CSV =================
  const handleExport = () => {

    const header = [
      "Candidate",
      "Job",
      "Application Status",
      "Applied Date",
      "Interview Status",
    ];

    const rows = filteredApplications.map((item) => [
      item.candidate,
      item.job,
      item.status,
      item.date,
      item.email,
    ]);

    const csv = [header, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = "applications-report.csv";

    link.click();

    URL.revokeObjectURL(url);

  };

  return (
    <div className="exporting-page">

      <section className="exporting-hero">

        <div className="hero-left">
          <h1>Exporting Data & Reports</h1>
          <p>
            Filter applications by job, status, and date range. Export the result to
            Excel-compatible format and review interview/report metrics.
          </p>
        </div>

        <button className="export-btn" type="button" onClick={handleExport}>
          <Download size={16} />
          Export Applications
        </button>

      </section>

      <section className="filter-panel">

        <div className="filter-field">
          <label>Job</label>
          <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
            {jobOptions.map((job) => (
              <option key={job} value={job}>
                {job}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label>From Date</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="filter-field">
          <label>To Date</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <button className="reset-btn" type="button" onClick={handleReset}>
          Reset Filters
        </button>

      </section>

      <section className="stats-grid">

        <article className="stat-card">
          <BarChart3 size={18} />
          <div>
            <span>Total Applications</span>
            <strong>{reportStats.total}</strong>
          </div>
        </article>

        <article className="stat-card">
          <FileSpreadsheet size={18} />
          <div>
            <span> Selected </span>
            <strong>{reportStats.hired}</strong>
          </div>
        </article>

        <article className="stat-card">
          <FileSpreadsheet size={18} />
          <div>
            <span>Rejected</span>
            <strong>{reportStats.rejected}</strong>
          </div>
        </article>

        <article className="stat-card">
          <CalendarCheck2 size={18} />
          <div>
            <span>Interviews (Scheduled / Completed)</span>
            <strong>
              {reportStats.scheduled} / {reportStats.completed}
            </strong>
          </div>
        </article>

      </section>

      <section className="table-card">

        <div className="table-title">
          <h2>Applications for Export</h2>
          <p>{filteredApplications.length} records selected</p>
        </div>

        <div className="table-head">
          <span>Candidate</span>
          <span>Job</span>
          <span>Application Status</span>
          <span>Applied Date</span>
          <span>Email</span>
        </div>

        <div className="table-body">

          {filteredApplications.map((item) => (

            <div className="table-row" key={item.id}>

              <span>{item.candidate}</span>

              <span>{item.job}</span>

              <span>{item.status}</span>

              <span>{item.date}</span>

              <span>{item.email}</span>

            </div>

          ))}

          {filteredApplications.length === 0 && (
            <p className="empty-state">No applications found for selected filters.</p>
          )}

        </div>

      </section>

    </div>
  );
};

export default ExportingData;
